<template>
  <div class="settings-page">
    <h2>Invite people</h2>
    <div class="subtitle">
      Invite your friends and colleagues to join Routine
    </div>
    <section>
      <div class="email">
        <FormInput
          ref="inputRef"
          v-model:value="email"
          type="email"
          placeholder="Email…"
          :state="errorInEmail ? 'error' : 'default'"
          @keydown="onInputKeydown"
        />
        <BaseButton
          class="invite"
          kind="main"
          type="primary"
          text="Invite"
          @click="inviteOne"
        />
      </div>

      <p v-if="sentMessage" class="success">
        {{ sentMessage }}
      </p>
      <p v-if="errorInEmail" class="error">
        Email is incorrect, please fix it and try again.
      </p>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, ref } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  components: {
    BaseButton,
    FormInput,
  },
  setup() {
    const controller = inject.controller();
    const email = ref("");
    const inputRef = ref<InstanceType<typeof FormInput> | null>(null);
    const errorInEmail = ref(false);
    const sentMessage = ref<string | null>(null);
    const sentMessageTimeoutId = ref<number | null>(null);
    const emailRegexp =
      /^([^<>()[\]\\,;:\s@"]+)@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/;

    const emailValid = (email: string) => !!email.match(emailRegexp);

    const focusInput = async () => {
      await nextTick();
      window.requestAnimationFrame(() => {
        inputRef.value?.focus?.();
      });
    };

    const showSentMessage = (invitedEmail: string) => {
      sentMessage.value = `Invite sent to ${invitedEmail}`;
      if (sentMessageTimeoutId.value != null) {
        window.clearTimeout(sentMessageTimeoutId.value);
      }
      sentMessageTimeoutId.value = window.setTimeout(() => {
        sentMessage.value = null;
        sentMessageTimeoutId.value = null;
      }, 2500);
    };

    onBeforeUnmount(() => {
      if (sentMessageTimeoutId.value != null) {
        window.clearTimeout(sentMessageTimeoutId.value);
      }
    });

    const inviteOne = async () => {
      const toInvite = email.value.trim();
      if (!toInvite || !emailValid(toInvite)) {
        errorInEmail.value = true;
        return;
      }
      errorInEmail.value = false;

      controller.inviteByEmail(toInvite);
      showSentMessage(toInvite);
      email.value = "";
      await focusInput();
    };

    const onInputKeydown = async (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      await inviteOne();
    };

    return {
      email,
      inputRef,
      errorInEmail,
      sentMessage,
      emailRegexp,
      inviteOne,
      onInputKeydown,
    };
  },
});
</script>
<style scoped lang="scss">
@use "../../styles/settings.scss";

p {
  @extend %font-body-regular;

  &.success {
    @extend %font-caption-regular;
    margin-top: 8px;
    @include themed() {
      color: t($primary);
    }
  }

  &.error {
    @include themed() {
      color: t($secondary);
    }
  }
}

.email {
  width: 100%;
  margin-bottom: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 12px;

  .base-button {
    flex-shrink: 0;
  }
}
</style>
